import { ROLE, isAdmin, isManager } from '@/models/role';

/**
 * Per-user capabilities, independent of `role`. Auth composes "tenant has the module" AND "user
 * holds the permission" server-side, so anything listed is usable as-is — never re-check module
 * access on top of it. Naming: `module:action`, where the module part is a tenant module id.
 */
export const PERMISSION = {
  GO_NO_GO_DECIDE: 'go-no-go:decide',
};

/** The permissions this app knows how to label; auth's catalogue may hold other apps' too. */
export const PERMISSIONS = Object.values(PERMISSION);

/** Fails closed: sessions cached before permissions shipped have no `permissions` key at all. */
export const hasPermission = (user, permission) => (user?.permissions ?? []).includes(permission);

/** `go-no-go:decide` → `permissions.go-no-go.decide`, holding `label` and `description`. */
export const permissionI18nKey = (id) => `permissions.${id.replace(':', '.')}`;

/**
 * Mirrors auth's guards on PUT / DELETE /users/{id}/permissions/{permissionId}, as UX only: manager and up, on oneself or
 * on a user whose role the caller could grant (`Role.canGrant`), so a manager can't touch the
 * tenant admin, Umaneo's root account in each tenant. Not `canEditRole`: there is no self or
 * platform-admin peer block here, and a platform admin may edit a tenant admin's permissions even
 * though no one assigns that role through the UI.
 */
export const canEditPermissions = (caller, target) => {
  if (!caller || !target || !isManager(caller.role)) return false;
  if (caller.id === target.id) return true;
  if (isAdmin(caller.role)) return true;
  return target.role === ROLE.MANAGER || target.role === ROLE.USER;
};

/**
 * What an admin can toggle for a user of the current tenant: registered in auth's `catalogue`,
 * labelled by this app, and either app-global or in a module the tenant has (auth rejects the rest).
 */
export const grantablePermissions = (catalogue, tenantModules) =>
  (catalogue ?? []).filter(
    (permission) =>
      PERMISSIONS.includes(permission.id) && (!permission.moduleId || tenantModules.includes(permission.moduleId)),
  );
